import type {
  PermissionSubject,
  PermissionSubjectMode,
} from '../composables/usePermissionSubjects';

export const filterPermissionSubjects = (
  items: PermissionSubject[],
  searchTerm: string
): PermissionSubject[] => {
  const term = searchTerm.trim().toLocaleLowerCase('pt-BR');
  if (!term) return [...items];

  return items.filter((item) => item.name.toLocaleLowerCase('pt-BR').includes(term));
};

export const sortPermissionSubjects = (
  items: PermissionSubject[],
  ascending: boolean
): PermissionSubject[] =>
  [...items].sort((a, b) =>
    ascending ? a.name.localeCompare(b.name, 'pt-BR') : b.name.localeCompare(a.name, 'pt-BR')
  );

export const resolveVisiblePermissionSubjects = (
  items: PermissionSubject[],
  searchTerm: string,
  ascending: boolean
): PermissionSubject[] =>
  sortPermissionSubjects(filterPermissionSubjects(items, searchTerm), ascending);

export const togglePermissionSubjectId = (selectedIds: string[], id: string): string[] =>
  selectedIds.includes(id)
    ? selectedIds.filter((selectedId) => selectedId !== id)
    : [...selectedIds, id];

export const areAllPermissionSubjectsSelected = (
  items: PermissionSubject[],
  selectedIds: string[]
): boolean => items.length > 0 && items.every((item) => selectedIds.includes(String(item.id)));

export const areSomePermissionSubjectsSelected = (
  items: PermissionSubject[],
  selectedIds: string[]
): boolean => items.some((item) => selectedIds.includes(String(item.id)));

export const permissionSubjectSelectAllLabel = (mode: PermissionSubjectMode): string =>
  mode === 'positions' ? 'Cargos' : 'Atividades Funcionais';

export const permissionSubjectSelectLabel = (mode: PermissionSubjectMode): string =>
  mode === 'positions' ? 'Selecione os cargos:' : 'Selecione as Atividades Funcionais:';

export const permissionSubjectCountLabel = (mode: PermissionSubjectMode): string =>
  mode === 'positions' ? 'Total de cargos selecionados:' : 'Total de atividades selecionadas:';

export const permissionSubjectEmptyMessage = (
  mode: PermissionSubjectMode,
  hasError: boolean,
  isSearching = false
): string => {
  if (hasError) return 'Erro ao carregar a lista.';
  if (isSearching) return 'Nenhum resultado encontrado';

  return mode === 'positions'
    ? 'Nenhum cargo encontrado.'
    : 'Nenhuma atividade funcional encontrada.';
};

export const PERMISSION_APPLY_SUCCESS_MESSAGE = 'Permissões aplicadas com sucesso.';
export const PERMISSION_APPLY_ERROR_MESSAGE = 'Erro ao aplicar permissões. Tente novamente';
export const PERMISSION_DIFFERENT_APPLY_TITLE =
  'Elementos com permissões diferentes foram selecionados. Deseja continuar?';
export const PERMISSION_DIFFERENT_APPLY_DESCRIPTION =
  'Ao aplicar novas permissões, as anteriores serão substituídas e a ação não pode ser desfeita.';

export const permissionSubjectClearTitle = (mode: PermissionSubjectMode): string =>
  mode === 'positions'
    ? 'Tem certeza que deseja limpar todos os Cargos selecionados?'
    : 'Tem certeza que deseja limpar todas as Atividades Funcionais selecionadas?';

export const PERMISSION_CLEAR_DESCRIPTION =
  'A ação não pode ser desfeita e as edições feitas serão perdidas.';

export const permissionSubjectClearSuccessMessage = (mode: PermissionSubjectMode): string =>
  mode === 'positions'
    ? 'Todos os Cargos selecionados foram limpos'
    : 'Todas as Atividades Funcionais selecionadas foram limpas';

export const PERMISSION_COPY_SUCCESS_MESSAGE = 'Permissões copiadas';
export const PERMISSION_PASTE_SUCCESS_MESSAGE = 'Permissões salvas com sucesso';
export const PERMISSION_PASTE_ERROR_MESSAGE = 'Erro ao salvar permissões. Tente novamente';

export const permissionPasteReplaceTitle = (targetCount: number): string =>
  targetCount === 1
    ? 'Você selecionou um elemento com permissões diferentes. As configurações anteriores serão substituídas ao aplicar novas permissões.'
    : 'Você selecionou elementos com permissões diferentes. As configurações anteriores serão substituídas ao aplicar novas permissões.';
